// /intake/questionnaire - the admin's editor for the client questionnaire.
// The whole form is one draft; one explicit Save sends it whole with the
// version it was loaded from, and every real save is audited server-side.

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { IntakeLanguage, IntakeTemplateResponse } from "@duralex/shared";
import {
  INTAKE_LIST_CAP,
  IntakeApiError,
  listIntakeSessions,
  translateIntakeTemplate,
} from "../api/intake";
import { useCurrentUser } from "../../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../../lib/roles";
import { Skeleton, SkeletonGroup } from "../../../components/chrome/Skeleton";
import { CategoriesEditor } from "../components/editor/CategoriesEditor";
import { ChecklistEditor } from "../components/editor/ChecklistEditor";
import { ConsentsEditor } from "../components/editor/ConsentsEditor";
import { PreviewDialog } from "../components/editor/PreviewDialog";
import { SectionsEditor } from "../components/editor/SectionsEditor";
import { BehindPills } from "../components/editor/fields";
import {
  aliveClaims,
  applyProposal,
  cellKey,
  cellsBefore,
  cellsToTranslate,
  chunkRows,
  discardDrafts,
  emptyOutcome,
  followRenames,
  machineConsentProblems,
  renamedKeys,
  runOutcomeLine,
  tally,
  toClaims,
  type Claim,
  type RunOutcome,
} from "../components/editor/translate";
import {
  SECOND_LANGUAGES,
  fromTemplate,
  isDirty,
  summarize,
  problems,
  toPut,
  type Draft,
} from "../components/editor/draft";
import {
  useIntakeTemplate,
  useResetIntakeTemplate,
  useSaveIntakeTemplate,
} from "../hooks/useIntakeTemplate";
import { fmtDate } from "../../../lib/dates";
import { languageName } from "../../../lib/intakeLanguages";
import {
  INTAKE_LANGUAGES,
  behindIn,
  missingTranslations,
  staleTranslations,
  templateLabels,
  type Behind,
} from "../lib/intakeLogic";

type Tab = "sections" | "categories" | "checklist" | "consents";

function toggle<T>(set: Set<T>, x: T, on: boolean): Set<T> {
  const next = new Set(set);
  if (on) next.add(x);
  else next.delete(x);
  return next;
}

/** After a save: the chrome languages the form is behind in, by how
    many labels (untranslated plus out of date). Null when none. */
function behindAfter(
  data: IntakeTemplateResponse,
): Array<{ lang: IntakeLanguage; behind: number }> | null {
  const missing = missingTranslations(fromTemplate(data.template));
  const list = data.chromeLanguages
    .filter((l) => l !== "en")
    .map((l) => ({ lang: l, behind: missing[l] + (data.translationHealth[l]?.stale.length ?? 0) }))
    .filter((x) => x.behind > 0);
  return list.length ? list : null;
}

/** Ids the loaded copy already had: set once, keyed by answers and files. */
function existingIds(t: IntakeTemplateResponse["template"]): Set<string> {
  const out = new Set<string>();
  for (const c of t.categories) out.add(`cat:${c.code}`);
  for (const s of t.sections) {
    out.add(`section:${s.id}`);
    for (const q of s.questions) out.add(`q:${q.id}`);
  }
  for (const d of t.checklist) out.add(`doc:${d.id}`);
  for (const c of t.consents) out.add(`consent:${c.id}`);
  return out;
}

function Gate({ user }: { user: ReturnType<typeof useCurrentUser> }) {
  return (
    <main className="cc-main">
      <div className="cc-body settings-body ie-body">
        <section className="cc-card">
          <h3>Intake questionnaire</h3>
          <p className="settings-sub">
            {user
              ? "Admin access is required to edit the questionnaire."
              : "Could not confirm your account. Refresh to retry."}
          </p>
        </section>
      </div>
    </main>
  );
}

export function IntakeTemplateEditor() {
  const user = useCurrentUser();
  const isAdmin = isAdminRole(user?.role);
  const tmpl = useIntakeTemplate(isAdmin);
  const save = useSaveIntakeTemplate();
  const reset = useResetIntakeTemplate();
  const sessions = useQuery({
    queryKey: ["intake", "sessions"],
    queryFn: listIntakeSessions,
    enabled: isAdmin,
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  // The version the draft came from - the save's precondition. Kept apart
  // from the query: a background refetch must not move it under a draft.
  const [loadedVersion, setLoadedVersion] = useState(0);
  const [lang, setLang] = useState<IntakeLanguage>("ru");
  const [tab, setTab] = useState<Tab>("sections");
  const [preview, setPreview] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [stale, setStale] = useState(false);
  // A model's unsaved proposals, the consents the office ticked as read,
  // the "also the out of date" ticks, the run in flight and its failure,
  // the offer made after a save, and a reload on its way.
  const [claims, setClaims] = useState<Claim[]>([]);
  const [read, setRead] = useState<Set<string>>(() => new Set());
  const [again, setAgain] = useState<Set<IntakeLanguage>>(() => new Set());
  const [run, setRun] = useState<{ lang: IntakeLanguage; total: number; done: number } | null>(
    null,
  );
  const [runError, setRunError] = useState<string | null>(null);
  // What the last run of each language came to; a new run of the language
  // starts it over, a rebase or a discard clears them all - the discard
  // even for a language whose cells the office had retyped since.
  const [outcome, setOutcome] = useState<Partial<Record<IntakeLanguage, RunOutcome>>>({});
  const [offer, setOffer] = useState<Array<{ lang: IntakeLanguage; behind: number }> | null>(null);
  const [reloading, setReloading] = useState(false);
  const stopRef = useRef(false);
  // The draft as the runner sees it between chunks: what the office typed
  // while a chunk was out is what the proposal is checked against. Set
  // in render, not in an effect: a click can land before an effect runs,
  // and a second language must start from what the first one wrote.
  const draftRef = useRef<Draft | null>(null);
  draftRef.current = draft;

  const rebase = (data: IntakeTemplateResponse) => {
    // A run in flight would write into a draft it did not start from.
    stopRef.current = true;
    setDraft(fromTemplate(data.template));
    setLoadedVersion(data.template.version);
    setServerErrors([]);
    setStale(false);
    setClaims([]);
    setRead(new Set());
    setRunError(null);
    setOffer(null);
    setOutcome({});
  };
  useEffect(() => {
    if (tmpl.data && draft === null) rebase(tmpl.data);
  }, [tmpl.data, draft]);

  const loaded = tmpl.data;
  const dirty = Boolean(loaded && draft && isDirty(loaded.template, draft));
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  if (!isAdmin) return <Gate user={user} />;

  const busy = save.isPending || reset.isPending || run !== null || reloading;
  const issues = loaded && draft ? problems(draft, loaded.reserved) : [];
  const missing = draft ? missingTranslations(draft) : null;
  const offered = missing ? INTAKE_LANGUAGES.filter((l) => missing[l] === 0) : [];
  const outdated =
    loaded && draft ? staleTranslations(loaded.template, loaded.translationHealth, draft) : null;
  // What the saved copy says a model wrote: the reset would destroy it.
  const machine = loaded
    ? Object.values(loaded.translationHealth).reduce((n, h) => n + (h?.machine ?? 0), 0)
    : 0;
  const alive = draft ? aliveClaims(draft, claims) : [];
  const aiKeys = new Set(alive.filter((c) => c.lang === lang).map((c) => c.key));
  const readKeys = new Set(
    alive.filter((c) => c.lang === lang && read.has(cellKey(c.key, c.lang))).map((c) => c.key),
  );
  const blocking = [...issues, ...machineConsentProblems(alive, read, languageName)];
  /** What the strip button would send - the run's own list, so the number
      on the button never counts a label the run leaves out. */
  const sendCount = (l: IntakeLanguage) =>
    draft && outdated ? cellsToTranslate(draft, l, outdated[l], again.has(l)).length : 0;
  const languageStatus = (l: IntakeLanguage) => {
    if (!missing || !outdated) return "";
    const parts = [missing[l] ? `${missing[l]} not translated` : "complete"];
    if (outdated[l].length) parts.push(`${outdated[l].length} out of date`);
    return parts.join(", ");
  };
  const dropping = loaded && missing ? loaded.languages.filter((l) => missing[l] > 0) : [];
  const staleSet = new Set(outdated?.[lang]);
  const labelCount = draft ? templateLabels(draft).length : 0;
  const openForms =
    sessions.data?.filter((s) => s.kind === "questionnaire" && s.status === "invited").length ?? 0;

  const onSave = () => {
    if (!draft) return;
    setServerErrors([]);
    save.mutate(toPut(draft, loadedVersion, toClaims(alive)), {
      onSuccess: (data) => {
        rebase(data);
        setOffer(behindAfter(data));
      },
      onError: (err) => {
        if (err instanceof IntakeApiError && err.status === 409) setStale(true);
        else if (err instanceof IntakeApiError && err.status === 400)
          setServerErrors(err.message.split("; "));
        else setServerErrors([err.message]);
      },
    });
  };
  const onDiscardDrafts = () => {
    if (!draft) return;
    setDraft(discardDrafts(draft, claims));
    setClaims([]);
    setRead(new Set());
    setOutcome({});
  };
  // One language, chunk after chunk; Stop lets the chunk in flight land
  // and sends no more. A refusal ends the run with the server's sentence;
  // what landed before it stays. The reply's skipped rows, notes and
  // warnings become the language's outcome line: a row the model left
  // alone stays empty and the button counts it again, and the line says
  // why.
  const translate = async (l: IntakeLanguage, includeStale: boolean): Promise<boolean> => {
    const d = draftRef.current;
    if (!d || !outdated) return true;
    const keys = cellsToTranslate(d, l, outdated[l], includeStale);
    const chunks = chunkRows(d, keys);
    if (!chunks.length) return true;
    const before = cellsBefore(d, l);
    const runId = crypto.randomUUID();
    stopRef.current = false;
    setRunError(null);
    setOutcome((prev) => {
      const next = { ...prev };
      delete next[l];
      return next;
    });
    setRun({ lang: l, total: chunks.length, done: 0 });
    let soFar = emptyOutcome(keys.length);
    try {
      for (let i = 0; i < chunks.length && !stopRef.current; i++) {
        const rows = chunks[i]!;
        const res = await translateIntakeTemplate({ from: "en", to: l, rows, runId });
        const current = draftRef.current;
        if (!current) break;
        const applied = applyProposal(current, l, rows, res, before);
        soFar = tally(soFar, rows, res, applied.claims.length);
        draftRef.current = applied.draft;
        setDraft(applied.draft);
        // A new proposal for a cell supersedes the old claim on it.
        setClaims((prev) => [
          ...prev.filter((c) => !applied.claims.some((n) => n.key === c.key && n.lang === c.lang)),
          ...applied.claims,
        ]);
        // A read tick was about the text it was given for; a new proposal
        // on the cell asks again.
        setRead((prev) => {
          const next = new Set(prev);
          for (const c of applied.claims) next.delete(cellKey(c.key, c.lang));
          return next;
        });
        setRun({ lang: l, total: chunks.length, done: i + 1 });
      }
      return true;
    } catch (err) {
      setRunError(err instanceof Error ? err.message : "The translation could not be made");
      return false;
    } finally {
      // Nothing to say before the first chunk came back: the alert says it.
      if (soFar.sent > 0) {
        const told = { ...soFar, stopped: stopRef.current };
        setOutcome((prev) => ({ ...prev, [l]: told }));
      }
      setRun(null);
    }
  };
  // The languages one after another; a Stop or a refusal during one of
  // them ends the queue, so the sentence on screen is about the language
  // that stopped.
  const translateAll = async (list: Array<{ lang: IntakeLanguage }>) => {
    stopRef.current = false;
    for (const x of list) {
      const ok = await translate(x.lang, true);
      if (!ok || stopRef.current) break;
    }
  };
  // An id or an option value retyped after a proposal landed moves the
  // cell; its claim and its read tick move with it.
  const onDraftChange = (next: Draft) => {
    if (draft && claims.length) {
      const moved = renamedKeys(draft, next);
      if (moved.size) {
        const followed = followRenames(moved, claims, read);
        setClaims(followed.claims);
        setRead(followed.read);
      }
    }
    setDraft(next);
  };
  // Nothing starts while the copy is on its way: a run begun here would
  // land on the rebased draft.
  const onReload = async () => {
    setReloading(true);
    try {
      const r = await tmpl.refetch();
      if (r.data) rebase(r.data);
    } finally {
      setReloading(false);
    }
  };
  const onReset = () => {
    if (
      !window.confirm(
        "Discard the office's copy of the questionnaire and go back to the shipped default? Its Russian comes back from the shipped file." +
          (machine
            ? ` It also throws away ${machine} machine translation${machine === 1 ? "" : "s"}.`
            : ""),
      )
    )
      return;
    reset.mutate(undefined, {
      onSuccess: ({ removed: _removed, ...data }) => rebase(data),
      onError: (err) => setServerErrors([err.message]),
    });
  };

  const status = save.isPending
    ? "Saving..."
    : reset.isPending
      ? "Resetting..."
      : stale
        ? "Someone else saved first"
        : dirty
          ? "Unsaved changes"
          : save.isSuccess
            ? "Saved"
            : "";

  return (
    <main className="cc-main">
      <div className="cc-body settings-body ie-body">
        <header className="settings-head">
          <div>
            <h2>Intake questionnaire</h2>
            <p className="settings-sub">
              What a client fills in from a link or the public form. Saved whole; every save is
              recorded in the audit log.
            </p>
            {loaded && (
              <p className="settings-sub" data-testid="template-provenance">
                {loaded.custom
                  ? `Version ${loaded.template.version}, saved ${fmtDate(loaded.saved?.at)} by ${
                      loaded.saved?.by ?? "an admin"
                    }`
                  : "Shipped default - nothing saved yet"}
              </p>
            )}
          </div>
          <div className="settings-save">
            <span className="settings-status" role="status">
              {status}
            </span>
            {/* Only what the server would refuse: an unread consent a model
                wrote changes nothing the card renders, so it blocks Save alone. */}
            <button
              type="button"
              className="cc-link-btn"
              disabled={!draft || issues.length > 0}
              title={issues.length > 0 ? "Fix the problems listed below first" : undefined}
              onClick={() => setPreview(true)}
            >
              Preview
            </button>
            {alive.length > 0 && (
              <button
                type="button"
                className="cc-link-btn"
                disabled={busy}
                onClick={onDiscardDrafts}
                title="Puts every cell a model wrote back to what it said before"
              >
                Discard AI drafts
              </button>
            )}
            <button
              type="button"
              className="cc-link-btn"
              disabled={!dirty || busy || !loaded}
              onClick={() => (stale ? void onReload() : loaded && rebase(loaded))}
              title={stale ? "Reloads the copy that was saved by someone else" : undefined}
            >
              Discard changes
            </button>
            <button
              type="button"
              className="settings-save-btn"
              disabled={!dirty || busy || !draft || blocking.length > 0}
              onClick={onSave}
            >
              Save changes
            </button>
          </div>
        </header>

        {offer && (
          <p className="ie-offer-line" role="status">
            {`Saved. ${languageName(offer[0]!.lang)} is behind by ${offer[0]!.behind} label${offer[0]!.behind === 1 ? "" : "s"}${offer
              .slice(1)
              .map((o) => `, ${languageName(o.lang)} by ${o.behind}`)
              .join("")}.`}
            <button
              type="button"
              className="cc-link-btn"
              disabled={busy}
              onClick={() => {
                const list = offer;
                setOffer(null);
                void translateAll(list);
              }}
            >
              Translate them with AI
            </button>
            <button type="button" className="cc-link-btn" onClick={() => setOffer(null)}>
              Not now
            </button>
          </p>
        )}

        {tmpl.isLoading && (
          <section className="cc-card">
            <SkeletonGroup label="Loading the questionnaire">
              <Skeleton width={220} height={20} />
              <Skeleton />
              <Skeleton width={180} />
            </SkeletonGroup>
          </section>
        )}
        {tmpl.isError && (
          <section className="cc-card">
            <p className="settings-sub">Could not load the questionnaire. Refresh to retry.</p>
          </section>
        )}

        {loaded && draft && missing && outdated && (
          <section className="cc-card ie-card">
            <div className="ie-toolbar">
              <div className="ie-offer">
                <span className="cc-form-label">
                  {dirty ? "Will be offered when you save" : "Offered to clients"}
                </span>
                <p className="ie-offered">{offered.map(languageName).join(", ")}</p>
              </div>
              <label className="cc-form-field ie-lang">
                <span className="cc-form-label">Second language shown</span>
                <select
                  className="cc-field"
                  value={lang}
                  onChange={(e) => setLang(e.target.value as IntakeLanguage)}
                >
                  {SECOND_LANGUAGES.map((l) => (
                    <option key={l} value={l}>
                      {`${languageName(l)} - ${languageStatus(l)}`}
                    </option>
                  ))}
                </select>
              </label>
              {openForms > 0 && (
                <p className="ie-hint" role="note">
                  {openForms === 1
                    ? "1 questionnaire is open right now; a change reaches it at its next save."
                    : `${openForms}${openForms >= INTAKE_LIST_CAP ? "+" : ""} questionnaires are open right now; a change reaches them at their next save.`}
                </p>
              )}
            </div>
            <ul className="ie-languages" aria-label="Translations">
              {SECOND_LANGUAGES.map((l) => {
                const saved = loaded.translationHealth[l];
                const begun = missing[l] < labelCount;
                const told = outcome[l];
                return (
                  <li key={l} className={begun ? undefined : "quiet"}>
                    <b>{languageName(l)}:</b> {languageStatus(l)}
                    {begun &&
                      !loaded.chromeLanguages.includes(l) &&
                      ` - the form's buttons and the client's e-mail have no ${languageName(l)} yet`}
                    {saved && saved.machine > 0 && (
                      <span className="ie-hint">
                        {` - ${saved.machine} of ${saved.translated} saved label${saved.translated === 1 ? "" : "s"} ${saved.machine === 1 ? "was" : "were"} machine-translated`}
                      </span>
                    )}
                    {run?.lang === l && (
                      <span className="ie-run" role="status">
                        {` - Translating ${languageName(l)} (${Math.min(run.done + 1, run.total)} of ${run.total})...`}
                        <button
                          type="button"
                          className="cc-link-btn"
                          onClick={() => {
                            stopRef.current = true;
                          }}
                        >
                          Stop
                        </button>
                      </span>
                    )}
                    {/* Mounted before the run ends, so the line's arrival is
                        announced; no role, so nothing counts it as a status. */}
                    <span aria-live="polite">{told ? ` - ${runOutcomeLine(told)}` : ""}</span>
                    {loaded.chromeLanguages.includes(l) &&
                      run?.lang !== l &&
                      (sendCount(l) > 0 || outdated[l].length > 0) && (
                        <span className="ie-run">
                          <button
                            type="button"
                            className="cc-link-btn"
                            disabled={busy || sendCount(l) === 0}
                            onClick={() => void translate(l, again.has(l))}
                          >
                            {sendCount(l) === 0
                              ? "Translate with AI"
                              : `Translate ${sendCount(l)} label${sendCount(l) === 1 ? "" : "s"} with AI`}
                          </button>
                          {outdated[l].length > 0 && (
                            <label className="cc-form-check">
                              <input
                                type="checkbox"
                                checked={again.has(l)}
                                disabled={busy}
                                onChange={(e) => setAgain(toggle(again, l, e.target.checked))}
                              />
                              {`also the ${outdated[l].length} out of date`}
                            </label>
                          )}
                        </span>
                      )}
                  </li>
                );
              })}
            </ul>
            {runError && (
              <p className="settings-error" role="alert">
                {runError}
              </p>
            )}
            {dropping.length > 0 && (
              <p className="settings-warn" role="alert">
                Saving drops {dropping.map(languageName).join(", ")} from the form:{" "}
                {dropping.map((l) => `${missing[l]} labels have no ${languageName(l)}`).join("; ")}.
              </p>
            )}
            {stale && (
              <div className="settings-error" role="alert">
                The questionnaire was saved by someone else since you opened it. Reload it, then
                make your changes again.{" "}
                <button
                  type="button"
                  className="cc-link-btn"
                  disabled={busy}
                  onClick={() => void onReload()}
                >
                  Reload
                </button>
              </div>
            )}
            {serverErrors.length > 0 && (
              <ul className="settings-error ie-problems" role="alert">
                {serverErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            )}
            {blocking.length > 0 && (
              <ul className="settings-error ie-problems" aria-label="Problems to fix before saving">
                {summarize(blocking).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
            {/* Each tab breaks the picker's number down to its part, so the
                office can find the rows behind "3 out of date". */}
            <div className="cc-tabs ie-tabs" role="tablist" aria-label="Questionnaire parts">
              {(
                [
                  [
                    "sections",
                    `Sections (${draft.sections.length})`,
                    behindIn({ sections: draft.sections }, lang, staleSet),
                  ],
                  [
                    "categories",
                    `Case types (${draft.categories.length})`,
                    behindIn({ categories: draft.categories }, lang, staleSet),
                  ],
                  [
                    "checklist",
                    `Documents (${draft.checklist.length})`,
                    behindIn({ checklist: draft.checklist }, lang, staleSet),
                  ],
                  [
                    "consents",
                    `Consents (${draft.consents.length})`,
                    behindIn({ consents: draft.consents }, lang, staleSet),
                  ],
                ] as Array<[Tab, string, Behind]>
              ).map(([id, label, behind]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={tab === id}
                  className={tab === id ? "active" : undefined}
                  onClick={() => setTab(id)}
                >
                  {label}
                  <BehindPills behind={behind} className="count-pill" />
                </button>
              ))}
            </div>
            {(() => {
              const props = {
                draft,
                lang,
                reserved: loaded.reserved,
                existing: existingIds(loaded.template),
                stale: staleSet,
                ai: aiKeys,
                onChange: onDraftChange,
              };
              if (tab === "sections") return <SectionsEditor {...props} />;
              if (tab === "categories") return <CategoriesEditor {...props} />;
              if (tab === "checklist") return <ChecklistEditor {...props} />;
              return (
                <ConsentsEditor
                  {...props}
                  read={readKeys}
                  onRead={(key, on) => setRead(toggle(read, cellKey(key, lang), on))}
                />
              );
            })()}
            {loaded.custom && (
              <p className="ie-reset">
                <button
                  type="button"
                  className="cc-link-btn danger"
                  disabled={busy}
                  onClick={onReset}
                >
                  Reset to the shipped default
                </button>
              </p>
            )}
          </section>
        )}
        {draft && (
          <PreviewDialog
            open={preview}
            onClose={() => setPreview(false)}
            template={{ version: loadedVersion, ...draft }}
            languages={["en", ...offered.filter((l) => l !== "en")]}
          />
        )}
      </div>
    </main>
  );
}
